'use client'

import cx from 'classnames'
import { DateTime } from 'luxon'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useSetRecoilState } from 'recoil'
import { z } from 'zod'

import PackableAccessoryList from '@/components/Accessories/PackableAccessoryList'
import FlashMessage from '@/components/Alert/FlashMessage'
import Cross from '@/components/Common/Cross'
import Toggle from '@/components/Common/Toggle'
import { loginUserState } from '@/components/Context/JwtTokenProvider'
import AmazonPayAdditionalPaymentButton from '@/components/Form/Atoms/AmazonPay/AmazonPayAdditionalPaymentButton'
import { i18n } from '@/components/Form/Atoms/DeliveryHourInput'
import { paymentMethodValues } from '@/components/Form/Atoms/PaymentMethodlnput'
import SubmitButton from '@/components/Form/Atoms/SubmitButton'
import CheckListProductForm from '@/components/Form/Organisms/CheckListProductForm'
import Picture from '@/components/Image/Picture'
import ContentModel from '@/components/LandingPage/ContentModel'
import Loading from '@/components/Loading/LoadingV2'
import '@/components/Order/SimpleOrderConfirm/SimpleOrderConfirm.scss'
import CartPageClientLogic from '@/components/Page/Cart/CartPage/CartPageClient/CartPageClientLogic'
import { useProcessResponse } from '@/components/hooks/useProcessResponse'
import { useEvent } from '@/components/hooks/useRecordEvent'
import { useZodResolver } from '@/components/hooks/useZodResolver'
import { confirmOrder } from '@/serverActions/confirmOrder'
import { getLoginSession } from '@/serverActions/getLoginSession'
import { OrderType } from '@/types/order'
import { UserType } from '@/types/user'
import { addBrowsingParameter } from '@/utils/urlUtils'

type Props = {
  loginUser: UserType
  order: OrderType | null
  onClick: () => void
}

const SimpleOrderConfirm = ({ loginUser, order, onClick }: Props) => {
  const [mount, setMount] = useState(false)
  const [openedInvoice, setOpenedInvoice] = useState(false)
  const [close, setClose] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [loadedAnimationFinish, setLoadedAnimationFinish] = useState(false)
  const buttonText = '商品の購入を確定する'

  useEffect(() => {
    setMount(true)
    return () => setMount(false)
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (order == null) {
        router.push(addBrowsingParameter(`/carts`))
      }
    }, 1000 * 5)
    return () => {
      clearTimeout(timer)
    }
  }, [mount, order])

  useEffect(() => {
    if (order != null) {
      setLoaded(true)
    }
  }, [order])

  useEffect(() => {
    let timer = null
    if (loaded) {
      timer = setTimeout(() => setLoadedAnimationFinish(true), 1000 * 1.2)
    }
    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [loaded])

  const { zodDynamicResolver } = useZodResolver()
  const form = useForm({
    mode: 'onSubmit',
    resolver: zodDynamicResolver(z.object({})),
    defaultValues: {},
  })

  const { record } = useEvent()
  const { processSuccessResponse, processErrorResponse } = useProcessResponse(form)
  const setLoginUser = useSetRecoilState(loginUserState)
  const router = useRouter()

  const ref = useRef<HTMLInputElement>(null)
  const handleSubmit = useCallback(async () => {
    if (order == null) {
      return
    }

    await record('click_purchase')

    const { success, errors } = await confirmOrder({
      orderId: order.orderId,
      fraudbuster: ref.current?.value,
    })
    if (success) {
      processSuccessResponse({
        after: async () => {
          const response = await getLoginSession()
          if (!(response == null || !response.success)) {
            setLoginUser(response.loginSession.user)
          }

          router.push(addBrowsingParameter(`/orders/${order.orderId}/complete`))
        },
      })
    } else {
      form.reset()
      processErrorResponse(errors, '注文の確定に失敗しました')
    }
  }, [order])

  return (
    <section
      id={'c-order-simpleOrderConfirm'}
      className={cx('c-order-simpleOrderConfirm', {
        'c-order-simpleOrderConfirm--mounted': mount,
        'c-order-simpleOrderConfirm--close': close,
      })}
    >
      <div
        className={cx('c-order-simpleOrderConfirm__modal', {
          'c-order-simpleOrderConfirm__modal--mounted': mount,
          'c-order-simpleOrderConfirm__modal--close': close,
        })}
      >
        <div
          className={cx('c-order-simpleOrderConfirm--loading', {
            'c-order-simpleOrderConfirm--loaded': loaded,
            'c-order-simpleOrderConfirm--loaded-AnimationFinish': loadedAnimationFinish,
          })}
        >
          <Loading center={true} />
        </div>
        <div className={cx('c-order-simpleOrderConfirm__modal-content')}>
          {loaded && (
            <p
              className={cx('c-order-simpleOrderConfirm__close')}
              onClick={() => {
                setClose(true)
                setTimeout(() => onClick(), 1000 * 1.7)
              }}
            >
              ×
            </p>
          )}

          <div className={cx('c-order-simpleOrderConfirm__content')}>
            <>
              <div className={cx('c-order-simpleOrderConfirm__content-main')}>
                {order != null && order.cart.reservationDescription != null && (
                  <div className="u-mb24">
                    <FlashMessage>{order.cart.reservationDescription}</FlashMessage>
                  </div>
                )}

                <div
                  style={{
                    paddingBottom: '2.5rem',
                  }}
                >
                  {order != null &&
                    order.cart.invoice.invoiceItems.map((invoiceItem) => {
                      const product = invoiceItem.cartProduct!
                      return (
                        <Fragment key={invoiceItem.cartProduct?.cartProductId}>
                          <div className={'c-order-simpleOrderConfirm__item-content'}>
                            <div className={'c-order-simpleOrderConfirm__item-content--title'}>
                              <Picture
                                src={product.creative.publishUrl}
                                width={product.creative.width}
                                height={product.creative.height}
                                alt={product.name}
                              />
                            </div>
                            <div
                              style={{
                                marginLeft: '5%',
                                width: '70%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                              }}
                            >
                              <h2
                                className={cx('u-font-yu-min')}
                                style={{
                                  fontSize: '1.5rem',
                                  lineHeight: '1.7rem',
                                }}
                              >
                                {product?.name}
                                <span
                                  className={cx('u-font-neue')}
                                  style={{
                                    color: 'rgba(6, 7, 37, 0.5)',
                                    display: 'block',
                                    fontSize: '1.2rem',
                                    letterSpacing: '0.04em',
                                  }}
                                >
                                  {product?.capacity}
                                </span>
                              </h2>
                              <div
                                style={{
                                  display: 'flex',
                                  gap: '.5rem',
                                  flexDirection: 'column',
                                }}
                              >
                                <div
                                  className={cx(
                                    'c-order-simpleOrderConfirm__item-content',
                                    'c-order-simpleOrderConfirm__item-content--inner'
                                  )}
                                >
                                  <p className={cx('u-font-yu-go', 'c-order-simpleOrderConfirm__item-text--small')}>
                                    個数
                                  </p>
                                  <p className={cx('c-order-simpleOrderConfirm__item-text')}>
                                    {invoiceItem.cartProduct?.quantity}個
                                  </p>
                                </div>
                                <div
                                  className={cx(
                                    'c-order-simpleOrderConfirm__item-content',
                                    'c-order-simpleOrderConfirm__item-content--inner'
                                  )}
                                >
                                  <p className={cx('u-font-yu-go', 'c-order-simpleOrderConfirm__item-text--small')}>
                                    商品小計
                                    <span className={cx('c-order-simpleOrderConfirm__item-text--label')}>[税込]</span>
                                  </p>
                                  <p className={cx('c-order-simpleOrderConfirm__item-text')}>
                                    ¥{product?.invoiceItem?.productSubtotalWithTax?.toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>

                          {product.cartContent?.type != 'html' && (
                            <ContentModel contentModel={product.cartContent} product={product} campaign={null} />
                          )}
                        </Fragment>
                      )
                    })}
                </div>

                {(order?.cart?.invoice?.packableAccessories || []).length > 0 ||
                (loginUser?.baseUser?.pointExchanges || []).length > 0 ? (
                  <div className={cx('c-order-simpleOrderConfirm__item')}>
                    <Toggle
                      ClickComponent={({ onClick, open }) => {
                        return (
                          <div onClick={onClick} className={cx('c-order-simpleOrderConfirm__toggle')}>
                            <h2 className={cx('c-order-simpleOrderConfirm__item-text', 'u-font-yu-min')}>特典</h2>
                            <div
                              className={cx('c-order-simpleOrderConfirm__toggle-cross', {
                                'c-order-simpleOrderConfirm__toggle-cross--open': open,
                              })}
                            >
                              <Cross open={open} />
                            </div>
                          </div>
                        )
                      }}
                    >
                      <PackableAccessoryList
                        packableAccessories={
                          order?.cart?.invoice?.packableAccessories?.filter((packableAccessory) => {
                            return packableAccessory.type == 'campaign' || loginUser != null
                          }) || []
                        }
                      />
                    </Toggle>
                  </div>
                ) : (
                  <></>
                )}

                <CartPageClientLogic initialCart={order?.cart!}>
                  {({ cart, sampleProducts, reload }) => {
                    const maxCheckSize = cart?.maxAbleToAddSampleCount || 0
                    const selectedProductIds = cart?.sampleProducts.map((product) => product.productId!) || []

                    return (
                      <>
                        {cart && sampleProducts.length > 0 && (
                          <div
                            className={cx(
                              'c-order-simpleOrderConfirm__item-content',
                              'c-order-simpleOrderConfirm__item'
                            )}
                          >
                            <div
                              className={cx('u-font-yu-min', 'c-order-simpleOrderConfirm__item-content--title')}
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                              }}
                            >
                              <div>
                                無料
                                <br />
                                サンプル
                              </div>
                              <div
                                style={{
                                  fontSize: '1rem',
                                  color: 'rgba(6, 7, 37, .6)',
                                }}
                              >
                                {selectedProductIds.length} / {maxCheckSize}
                              </div>
                            </div>
                            <div className={cx('c-order-simpleOrderConfirm__item-content--description')}>
                              <CheckListProductForm
                                products={sampleProducts}
                                afterSubmit={() => reload(false)}
                                selectedProductIds={selectedProductIds}
                              />
                            </div>
                          </div>
                        )}
                      </>
                    )
                  }}
                </CartPageClientLogic>

                {order != null && (
                  <>
                    <Link
                      href={'/orders'}
                      className={cx('c-order-simpleOrderConfirm__item-content', 'c-order-simpleOrderConfirm__item')}
                    >
                      <div
                        className={cx(
                          'c-order-simpleOrderConfirm__item-content',
                          'c-order-simpleOrderConfirm__item-arrow'
                        )}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 7 6" fill="#060725">
                          <path d="M7 3L3.09968e-07 6L2.33333 3.00001L-4.76837e-07 9.17939e-07L7 3Z"></path>
                        </svg>
                      </div>
                      <div className={cx('u-font-yu-min', 'c-order-simpleOrderConfirm__item-content--title')}>
                        お届け先
                        <br />
                        情報
                      </div>
                      <div className={cx('c-order-simpleOrderConfirm__item-content--description')}>
                        <div className={cx('u-mb8', 'c-order-simpleOrderConfirm__item-text--small')}>
                          <div className={cx('u-mb4')}>
                            {order.shippingAddress.lastName} {order.shippingAddress.firstName}
                          </div>
                          <div
                            className={'u-mb4'}
                            style={{
                              lineHeight: '1.5rem',
                            }}
                          >
                            〒{order.shippingAddress.postalCode} {order.shippingAddress.prefecture}
                            {order.shippingAddress.city}
                            {order.shippingAddress.addressLine}
                          </div>
                        </div>
                        <div className={cx('u-mb8', 'c-order-simpleOrderConfirm__item-text--small')}>
                          <div className={cx('u-mb4')}>
                            お届け{' '}
                            {order.cart.reservationDescription ||
                              (order.deliveryDate != 'shortest'
                                ? DateTime.fromISO(order.deliveryDate).toFormat('yyyy年MM月dd日')
                                : '最短')}{' '}
                            {i18n(order.deliveryHour)}
                          </div>
                        </div>
                      </div>
                    </Link>
                    <Link
                      href={'/orders/confirm#payment'}
                      className={cx('c-order-simpleOrderConfirm__item-content', 'c-order-simpleOrderConfirm__item')}
                    >
                      <div
                        className={cx(
                          'c-order-simpleOrderConfirm__item-content',
                          'c-order-simpleOrderConfirm__item-arrow'
                        )}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 7 6" fill="#060725">
                          <path d="M7 3L3.09968e-07 6L2.33333 3.00001L-4.76837e-07 9.17939e-07L7 3Z"></path>
                        </svg>
                      </div>
                      <div className={cx('u-font-yu-min', 'c-order-simpleOrderConfirm__item-content--title')}>
                        お支払い
                        <br />
                        情報
                      </div>
                      <div
                        className={cx(
                          'c-order-simpleOrderConfirm__item-content--description',
                          'c-order-simpleOrderConfirm__item-content--description-payment'
                        )}
                        style={{
                          ...(order.paymentMethod == 'credit' ? {} : { display: 'flex' }),
                        }}
                      >
                        {paymentMethodValues[order.paymentMethod]}
                        {order.paymentMethod == 'credit' && loginUser?.creditCard?.cardNo && (
                          <p className={cx('u-mt4')}>{loginUser?.creditCard?.cardNo}</p>
                        )}
                      </div>
                    </Link>

                    <div
                      className={cx('c-order-simpleOrderConfirm__item', 'c-order-simpleOrderConfirm__item--price')}
                      onClick={() => setOpenedInvoice(true)}
                    >
                      {openedInvoice ? (
                        <div>
                          <div
                            className={cx(
                              'c-order-simpleOrderConfirm__item-content',
                              'c-order-simpleOrderConfirm__item-content--inner',
                              'u-mb20'
                            )}
                          >
                            <p
                              className={cx(
                                'c-order-simpleOrderConfirm__item-content',
                                'c-order-simpleOrderConfirm__item-text'
                              )}
                            >
                              商品合計金額
                              <span
                                className={cx(
                                  'c-order-simpleOrderConfirm__item-text',
                                  'c-order-simpleOrderConfirm__item-text--label'
                                )}
                              >
                                [税込]
                              </span>
                            </p>
                            <p
                              className={cx(
                                'c-order-simpleOrderConfirm__item-content',
                                'c-order-simpleOrderConfirm__item-text'
                              )}
                            >
                              ¥{order.cart.invoice.productTotalWithTax.toLocaleString()}
                            </p>
                          </div>

                          <div
                            className={cx(
                              'c-order-simpleOrderConfirm__item-content',
                              'c-order-simpleOrderConfirm__item-content--inner',
                              'u-mb40'
                            )}
                          >
                            <p
                              className={cx(
                                'c-order-simpleOrderConfirm__item-content',
                                'c-order-simpleOrderConfirm__item-text'
                              )}
                            >
                              送料
                              <span
                                className={cx(
                                  'c-order-simpleOrderConfirm__item-text',
                                  'c-order-simpleOrderConfirm__item-text--label'
                                )}
                              >
                                [税込]
                              </span>
                            </p>
                            <p
                              className={cx(
                                'c-order-simpleOrderConfirm__item-content',
                                'c-order-simpleOrderConfirm__item-text'
                              )}
                            >
                              ¥{order.cart.invoice.postageWithTax.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div
                          className={cx(
                            'c-order-simpleOrderConfirm__item-content',
                            'c-order-simpleOrderConfirm__item-arrow'
                          )}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="7"
                            viewBox="0 0 7 6"
                            fill="#060725"
                          >
                            <path d="M7 3L3.09968e-07 6L2.33333 3.00001L-4.76837e-07 9.17939e-07L7 3Z"></path>
                          </svg>
                        </div>
                      )}

                      <div
                        className={cx(
                          'c-order-simpleOrderConfirm__item-content',
                          'c-order-simpleOrderConfirm__item-content--inner'
                        )}
                      >
                        <div
                          className={cx(
                            'c-order-simpleOrderConfirm__item-text',
                            'c-order-simpleOrderConfirm__item-text--bold'
                          )}
                        >
                          合計金額
                          <span
                            className={cx(
                              'c-order-simpleOrderConfirm__item-text',
                              'c-order-simpleOrderConfirm__item-text--label'
                            )}
                          >
                            [税込]
                          </span>
                        </div>
                        <div
                          className={cx(
                            'c-order-simpleOrderConfirm__item-text',
                            'c-order-simpleOrderConfirm__item-text--bold'
                          )}
                        >
                          ¥{order.cart.invoice.billingAmountWithTax.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
              {order != null && (
                <>
                  <div className={'u-mb8'}>
                    <FormProvider {...form}>
                      <form onSubmit={form.handleSubmit(handleSubmit)}>
                        <input type="hidden" id="fraudbuster" name="fraudbuster" ref={ref} />
                        {order.paymentMethod == 'amazonpay' ? (
                          <AmazonPayAdditionalPaymentButton
                            orderId={order.orderId}
                            redirectUrl={`orders/confirm/amazonPay/checkout/callback?orderId=${order.orderId}`}
                            productType={'PayOnly'}
                          >
                            <p style={{ fontSize: '1.5rem', textAlign: 'center' }}>Amazon アカウントでお支払い</p>
                          </AmazonPayAdditionalPaymentButton>
                        ) : (
                          <SubmitButton width="100%" allowDirty={true}>
                            {buttonText}
                          </SubmitButton>
                        )}
                      </form>
                    </FormProvider>
                  </div>

                  {order.paymentMethod != 'amazonpay' && (
                    <p className={'u-help'}>「{buttonText}」をボタンを押すとご注文が確定いたします。</p>
                  )}
                </>
              )}
            </>
          </div>
        </div>
      </div>
    </section>
  )
}

export default SimpleOrderConfirm
